import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { 
  Wine, GlassWater, PartyPopper, Calendar, Users, MapPin, Phone, Mail, 
  CheckCircle, Star, ChevronRight, Loader2, Beer, Sparkles, Clock,
  Facebook, ArrowRight, Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Your actual images
const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_d8676ea2-8bbd-4c2e-b383-1e90d257dd0e/artifacts/7blkbm8t_IMG_1359.jpeg',
  logo: 'https://customer-assets.emergentagent.com/job_d8676ea2-8bbd-4c2e-b383-1e90d257dd0e/artifacts/y5p8r7ld_IMG_1360.jpeg',
  event: 'https://customer-assets.emergentagent.com/job_d8676ea2-8bbd-4c2e-b383-1e90d257dd0e/artifacts/hqhgwkx5_IMG_1362.jpeg'
};

const CocktailCoachWebsite = () => {
  const { toast } = useToast();
  const [showInquiry, setShowInquiry] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_date: '',
    event_type: '',
    guest_count: '',
    location: '',
    package_interest: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.event_date) {
      toast({ title: 'Error', description: 'Please fill in required fields', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/quote-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: 'cocktail_coach_website',
          service_type: 'cocktail_coach',
          package_interest: selectedPackage || formData.package_interest
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast({ title: 'Cheers!', description: "Your inquiry has been submitted. We'll be in touch soon!" });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to submit inquiry. Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const packages = [
    {
      name: 'The Essentials Bar',
      price: '$750',
      tagline: 'Simple. Stylish. Stress-free.',
      description: 'Perfect for small parties, birthdays, or backyard gatherings.',
      features: [
        'Fully Licensed & Insured',
        '1 Professional Smart Serve Certified Bartender',
        'Up to 75 guests | 6 hours service',
        'Mobile Bar Setup with basic décor',
        'Ice, Garnishes, Basic Mixers & Disposables',
        'Domestic Beer, Wine & Mixed Drinks',
        'Host, cash, toonie or ticket bar options'
      ],
      popular: false
    },
    {
      name: 'The Signature Bar Experience',
      price: '$950',
      tagline: 'Our most popular package',
      description: 'Designed for weddings, celebrations & corporate events.',
      features: [
        'Everything in Essentials, PLUS:',
        '2 Professional Bartenders',
        'Up to 150 guests | 6 hours service',
        'Premium Mobile Bar Setup with themed décor',
        'Premium Mixers & Disposables',
        '2 Signature Cocktails of your choice',
        'Personalized drink menu display'
      ],
      popular: true
    },
    {
      name: 'The Luxe Bar Experience',
      price: '$1,250',
      tagline: 'Where presentation meets perfection',
      description: 'For upscale events where every detail shines.',
      features: [
        'Everything in Signature, PLUS:',
        '3 Professional Bartenders',
        'Up to 200 guests | 6 hours service',
        'Themed Chalkboard Menu',
        'Lighting & Outdoor Bar Seating',
        'Additional Wine & Beer Station',
        'Mocktail & Non-Alcoholic Station',
        'Pre-Event Consultation & Menu Design'
      ],
      popular: false
    }
  ];

  const drinks = {
    domestic_beer: ['Bud Light', 'Budweiser', 'Michelob Ultra', 'Blue'],
    premium_beer: ["Alexander Keith's", 'Stella', 'Sons of Kent (Local)', 'Steam Whistle'],
    rail_spirits: ['Smirnoff Vodka', 'Beefeater Gin', 'Captain Morgan White Rum', 'Captain Morgan Spiced Rum', 'Crown Royal Whiskey', 'Sauza Tequila'],
    premium_spirits: ['Ketel One Vodka', 'Tanqueray Gin', 'Bacardi White Rum', 'Gibson Sterling Whiskey', 'Jose Cuervo Tequila'],
    house_wine: ['Seasons Pinot Grigio', 'Seasons Cabernet Merlot', 'Seasons Rosé', 'Seasons Prosecco'],
    premium_wine: ['Lakeview Chardonnay', 'Lakeview Black Cabernet'],
    liqueurs: ['Baileys', 'Amaretto', 'Fireball', 'Tequila Rose', 'Blue Curaçao', 'Triple Sec'],
    cocktails: ['Classic Mojito', 'Margarita', 'Old Fashioned', 'Cosmopolitan', 'Martini & More'],
    non_alcoholic: ['Pepsi', 'Diet Pepsi', 'Gingerale', '7-up', 'Lemonade', 'Orange Juice', 'Cranberry Juice', 'Mocktails']
  };

  const openInquiryWithPackage = (pkg) => {
    setSelectedPackage(pkg);
    setFormData({ ...formData, package_interest: pkg });
    setShowInquiry(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <div className="flex items-center gap-3">
              <img 
                src={IMAGES.logo} 
                alt="Cocktail Coach Bar Logo" 
                className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover"
              />
              <div className="hidden sm:block">
                <span className="text-lg font-semibold text-gray-900">Cocktail Coach</span>
                <p className="text-xs text-gray-500 -mt-1">by Willow Ridge Golf & Country Club</p>
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-8">
              <a href="#packages" className="text-gray-600 hover:text-teal-600 transition-colors font-medium">Packages</a>
              <a href="#about" className="text-gray-600 hover:text-teal-600 transition-colors font-medium">About</a>
              <a href="#drinks" className="text-gray-600 hover:text-teal-600 transition-colors font-medium">Menu</a>
              <a 
                href="https://www.facebook.com/share/1DZSyaYy5x/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-teal-600 transition-colors"
                data-testid="nav-facebook-link"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <Button 
                onClick={() => setShowInquiry(true)} 
                className="bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6"
              >
                Book Your Bar
              </Button>
            </div>
            <div className="flex lg:hidden items-center gap-3">
              <a 
                href="https://www.facebook.com/share/1DZSyaYy5x/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-teal-600"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <Button 
                onClick={() => setShowInquiry(true)} 
                size="sm"
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Light Background */}
      <section className="pt-24 md:pt-28 pb-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2 mb-6">
                <Sparkles className="h-4 w-4 text-teal-600" />
                <span className="text-teal-700 text-sm font-medium">Mobile Bartending Services</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Your Event, Our Bar,
                <span className="block text-teal-600">Infinite Memories</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Bringing the perfect bar experience to your special occasion. Fully licensed, 
                insured, and staffed by certified professional bartenders.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  onClick={() => setShowInquiry(true)} 
                  size="lg"
                  className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6 font-semibold group"
                  data-testid="hero-cta-btn"
                >
                  Book Your Bar Experience
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 text-lg px-8 py-6"
                  onClick={() => document.getElementById('packages').scrollIntoView({ behavior: 'smooth' })}
                >
                  View Packages
                </Button>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-teal-500" />
                  <span>Fully Licensed & Insured</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-teal-500" />
                  <span>Smart Serve Certified</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={IMAGES.hero} 
                alt="Cocktail Coach mobile bar at event" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg border border-gray-100 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <Clock className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-lg">6 Hours</p>
                    <p className="text-gray-500 text-sm">Included in all packages</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It <span className="text-teal-600">Works</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Four simple steps to the perfect bar service for your event
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Calendar, title: 'Book Your Date', desc: 'Choose your event date and preferred package' },
              { icon: GlassWater, title: 'Customize Menu', desc: 'Select drinks and signature cocktails' },
              { icon: Users, title: 'We Arrive Ready', desc: 'Our team sets up before your guests arrive' },
              { icon: PartyPopper, title: 'Enjoy the Party', desc: 'We handle the bar, you enjoy the event' }
            ].map((step, index) => (
              <div key={index} className="relative text-center">
                <div className="relative mb-6 inline-block">
                  <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto">
                    <step.icon className="h-8 w-8 text-teal-600" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2 mb-4">
              <Star className="h-4 w-4 text-teal-600" />
              <span className="text-teal-700 text-sm font-medium">Our Packages</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose Your <span className="text-teal-600">Bar Experience</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Three perfectly crafted packages to match any event size and style
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden bg-white border-gray-200 hover:border-teal-300 transition-all duration-300 hover:shadow-xl ${
                  pkg.popular ? 'ring-2 ring-teal-500 scale-105' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-4 right-4 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-teal-600 font-medium mb-4">{pkg.tagline}</p>
                  
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-4xl font-bold text-gray-900">{pkg.price}</span>
                    <span className="text-gray-500">flat fee</span>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{pkg.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-700">
                        <CheckCircle className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    onClick={() => openInquiryWithPackage(pkg.name)}
                    className={`w-full py-6 font-semibold ${
                      pkg.popular 
                        ? 'bg-teal-500 hover:bg-teal-600 text-white' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                    data-testid={`package-cta-${index}`}
                  >
                    Select This Package
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Need a custom package? We love creating unique experiences!
            </p>
            <Button 
              variant="outline" 
              onClick={() => setShowInquiry(true)}
              className="border-teal-500 text-teal-600 hover:bg-teal-50"
            >
              Request Custom Quote
            </Button>
          </div>
        </div>
      </section>

      {/* About Section with Event Image */}
      <section id="about" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src={IMAGES.event}
                alt="Cocktail Coach at an outdoor event"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-teal-600">Cocktail Coach?</span>
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                As part of Willow Ridge Golf & Country Club, we bring years of hospitality excellence 
                to your special event. Our mobile bar service combines professional bartending with 
                the flexibility to serve anywhere in Chatham, Blenheim & surrounding areas.
              </p>
              
              <div className="space-y-6">
                {[
                  { icon: Star, title: 'Fully Licensed & Insured', desc: 'Complete peace of mind with AGCO licensed service' },
                  { icon: Users, title: 'Smart Serve Certified', desc: 'Every bartender is professionally trained and certified' },
                  { icon: Heart, title: 'Custom Experiences', desc: 'Signature cocktails and themed bars tailored to you' },
                  { icon: CheckCircle, title: 'Setup & Cleanup', desc: 'We handle everything so you can enjoy the party' }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Drinks Menu Preview */}
      <section id="drinks" className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2 mb-4">
              <Beer className="h-4 w-4 text-teal-600" />
              <span className="text-teal-700 text-sm font-medium">Our Selection</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Drinks to Delight <span className="text-teal-600">Every Guest</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Quality beverages from trusted brands. Plus, we can create custom signature cocktails for your special event!
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Beer */}
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <Beer className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-3">Beer Selection</h3>
                <div className="mb-3">
                  <p className="text-xs text-gray-500 font-medium mb-1">Domestic</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    {drinks.domestic_beer.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">Premium/Craft</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    {drinks.premium_beer.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
            
            {/* Spirits */}
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <GlassWater className="h-6 w-6 text-teal-600" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-3">Spirits</h3>
                <div className="mb-3">
                  <p className="text-xs text-gray-500 font-medium mb-1">Rail</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    {drinks.rail_spirits.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">Premium</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    {drinks.premium_spirits.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
            
            {/* Wine */}
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center mb-4">
                  <Wine className="h-6 w-6 text-rose-600" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-3">Wine</h3>
                <div className="mb-3">
                  <p className="text-xs text-gray-500 font-medium mb-1">House Wine</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    {drinks.house_wine.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium mb-1">Premium Wine</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    {drinks.premium_wine.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
            
            {/* Cocktails */}
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-3">Cocktails</h3>
                <ul className="space-y-1 text-gray-600 text-sm mb-3">
                  {drinks.cocktails.map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span> {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-teal-600 font-medium">+ Custom Signature Cocktails available!</p>
              </CardContent>
            </Card>
            
            {/* Liqueurs */}
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <GlassWater className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-3">Liqueurs & Extras</h3>
                <ul className="space-y-1 text-gray-600 text-sm">
                  {drinks.liqueurs.map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span> {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-500 mt-2">+ Flavoured syrups available</p>
              </CardContent>
            </Card>
            
            {/* Non-Alcoholic */}
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <GlassWater className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-3">Non-Alcoholic</h3>
                <ul className="space-y-1 text-gray-600 text-sm">
                  {drinks.non_alcoholic.map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span> {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-teal-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Elevate Your Event?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Let's create an unforgettable bar experience for your next celebration
          </p>
          <Button 
            onClick={() => setShowInquiry(true)}
            size="lg"
            className="bg-white text-teal-600 hover:bg-gray-100 text-lg px-10 py-6 font-semibold"
            data-testid="final-cta-btn"
          >
            Book Your Bar Experience Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-teal-200 mt-6 text-sm">
            Booking now for 2026 | Chatham, Blenheim & Surrounding Areas
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src={IMAGES.logo} 
                  alt="Cocktail Coach Bar Logo" 
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div>
                  <span className="text-lg font-bold text-white">Cocktail Coach</span>
                  <p className="text-xs text-gray-400">by Willow Ridge Golf & Country Club</p>
                </div>
              </div>
              <p className="text-gray-400 mb-4 max-w-sm">
                Professional mobile bartending services for weddings, corporate events, 
                and private celebrations throughout Chatham-Kent.
              </p>
              <div className="flex gap-4">
                <a 
                  href="https://www.facebook.com/share/1DZSyaYy5x/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 hover:bg-teal-500 rounded-full flex items-center justify-center transition-colors"
                  data-testid="footer-facebook-link"
                >
                  <Facebook className="h-5 w-5 text-white" />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <a href="#packages" className="block text-gray-400 hover:text-teal-400 transition-colors">Packages</a>
                <a href="#about" className="block text-gray-400 hover:text-teal-400 transition-colors">About Us</a>
                <a href="#drinks" className="block text-gray-400 hover:text-teal-400 transition-colors">Drink Menu</a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <div className="space-y-3 text-gray-400">
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-teal-400" /> (519) 676-8772
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-teal-400" /> info@willowridgegolf.ca
                </p>
                <p className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-teal-400 mt-1 flex-shrink-0" /> 
                  <span>439 Chatham Street South<br />Blenheim, Ontario</span>
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="text-center mb-4">
              <p className="text-gray-300 font-medium">Owned & Operated by Willow Ridge Golf & Country Club</p>
              <p className="text-gray-500 text-sm">439 Chatham Street South, Blenheim, Ontario</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm">
                © 2026 Cocktail Coach Bar. All rights reserved.
              </p>
              <p className="text-gray-500 text-sm">
                Fully Licensed & Insured | Smart Serve Certified
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Inquiry Modal */}
      {showInquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white">
            <CardContent className="p-6">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-teal-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Cheers!</h3>
                  <p className="text-gray-600 mb-6">Your inquiry has been submitted. We'll contact you within 24 hours to discuss your event.</p>
                  <Button 
                    onClick={() => { setShowInquiry(false); setSubmitted(false); setSelectedPackage(null); }} 
                    className="bg-teal-500 hover:bg-teal-600"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Book Your Bar</h3>
                      {selectedPackage && (
                        <p className="text-teal-600 text-sm mt-1">Selected: {selectedPackage}</p>
                      )}
                    </div>
                    <button 
                      onClick={() => { setShowInquiry(false); setSelectedPackage(null); }} 
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-700">Your Name *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="border-gray-300"
                          data-testid="inquiry-name"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-700">Email *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="border-gray-300"
                          data-testid="inquiry-email"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-700">Phone</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="border-gray-300"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-700">Event Date *</Label>
                        <Input
                          type="date"
                          value={formData.event_date}
                          onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                          required
                          className="border-gray-300"
                          data-testid="inquiry-date"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-700">Event Type</Label>
                        <Select value={formData.event_type} onValueChange={(v) => setFormData({ ...formData, event_type: v })}>
                          <SelectTrigger className="border-gray-300">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="wedding">Wedding</SelectItem>
                            <SelectItem value="corporate">Corporate Event</SelectItem>
                            <SelectItem value="birthday">Birthday Party</SelectItem>
                            <SelectItem value="anniversary">Anniversary</SelectItem>
                            <SelectItem value="backyard">Backyard Party</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-700">Expected Guests</Label>
                        <Input
                          type="number"
                          value={formData.guest_count}
                          onChange={(e) => setFormData({ ...formData, guest_count: e.target.value })}
                          placeholder="75"
                          className="border-gray-300"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-gray-700">Event Location</Label>
                      <Input
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="City or venue name"
                        className="border-gray-300"
                      />
                    </div>
                    
                    {!selectedPackage && (
                      <div>
                        <Label className="text-gray-700">Package Interest</Label>
                        <Select value={formData.package_interest} onValueChange={(v) => setFormData({ ...formData, package_interest: v })}>
                          <SelectTrigger className="border-gray-300">
                            <SelectValue placeholder="Select a package..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="The Essentials Bar">The Essentials Bar ($750)</SelectItem>
                            <SelectItem value="The Signature Bar Experience">The Signature Bar Experience ($950)</SelectItem>
                            <SelectItem value="The Luxe Bar Experience">The Luxe Bar Experience ($1,250)</SelectItem>
                            <SelectItem value="custom">Custom Package</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-gray-700">Tell Us About Your Event</Label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Any special requests, signature cocktail ideas, or questions?"
                        rows={3}
                        className="border-gray-300"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-6"
                      data-testid="submit-inquiry-btn"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Submit Inquiry
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CocktailCoachWebsite;
